import { ChangeDetectionStrategy, Component, OnInit, computed, effect, signal } from '@angular/core';
import { RouterLink } from '@angular/router';
import { MusicDataService } from '../music-data.service';
import { FavouriteAlbumsService, toGridAlbum } from '../favourite-albums.service';
import { AlbumGridComponent } from '../shared/album-grid.component';
import { AlbumSortControlsComponent } from '../shared/album-sort-controls.component';
import { PagerComponent } from '../shared/pager.component';
import { AlbumSort, filterAlbumsByYear, matchesQuery, pageCount, pageSlice, releaseYears, sortAlbums, yearOptions } from '../shared/album-list';
import { loadSavedState, saveState } from '../shared/saved-state';

const STATE_KEY = 'favouriteAlbums.filters';
const SORTS: AlbumSort[] = ['top', 'newest', 'oldest'];
const DEFAULT_STATE = { query: '', genre: -1, sort: 'top', year: 0, page: 1 }; // genre -1 = all genres

// Same sort/year/paging mechanism as Top Albums, but over the whole saved pool (up to 1000 albums)
export const FAVOURITES_PAGE_SIZE = 20;
export const FAVOURITES_MAX_PAGES = 50;

@Component({
  changeDetection: ChangeDetectionStrategy.OnPush,
  selector: 'app-favourite-albums',
  templateUrl: './favourite-albums.component.html',
  styleUrls: ['./favourite-albums.component.css'],
  imports: [RouterLink, AlbumGridComponent, AlbumSortControlsComponent, PagerComponent]
})
export class FavouriteAlbumsComponent implements OnInit {
  readonly years = yearOptions();

  private saved = loadSavedState(STATE_KEY, DEFAULT_STATE);

  query = signal(this.saved.query);
  genre = signal(this.saved.genre);
  sort = signal<AlbumSort>(SORTS.includes(this.saved.sort as AlbumSort) ? this.saved.sort as AlbumSort : 'top');
  year = signal(this.years.includes(this.saved.year) ? this.saved.year : 0);
  page = signal(Math.max(1, Math.floor(this.saved.page) || 1));

  loading = signal(true);
  error = signal<string | null>(null);
  genreNames = signal<Record<number, string>>({});

  // The whole saved pool, in the grid's album shape
  pool = computed(() => (this.favourites.albums() ?? []).map(toGridAlbum));

  // Years that have saved albums; other years are disabled in the dropdown
  availableYears = computed(() => releaseYears(this.pool()));

  // Genres that actually appear in the pool, as filter chips
  genreOptions = computed(() => {
    const present = new Set(this.pool().map(a => a.genre_id as number));
    const names = this.genreNames();
    return [...present]
      .filter(id => id >= 0 && names[id])
      .map(id => ({ id, name: names[id] }))
      .sort((a, b) => a.name.localeCompare(b.name));
  });

  // Search, genre and year narrow the pool; sort orders it ("most popular" = most fans)
  filtered = computed(() => {
    let list = this.pool();
    const q = this.query().trim();
    if (q) list = list.filter(a => matchesQuery(a, q));
    if (this.genre() >= 0) list = list.filter(a => a.genre_id === this.genre());
    list = filterAlbumsByYear(list, this.year());
    return this.sort() === 'top'
      ? [...list].sort((a, b) => (b.fans ?? 0) - (a.fans ?? 0))
      : sortAlbums(list, this.sort());
  });
  pages = computed(() => pageCount(this.filtered().length, FAVOURITES_PAGE_SIZE, FAVOURITES_MAX_PAGES));
  visible = computed(() => pageSlice(this.filtered(), this.page(), FAVOURITES_PAGE_SIZE));

  constructor(private musicdata: MusicDataService, protected favourites: FavouriteAlbumsService) {
    effect(() => {
      saveState(STATE_KEY, {
        query: this.query(), genre: this.genre(), sort: this.sort(), year: this.year(), page: this.page()
      });
    });
    // Removing albums can shrink the list below the current page
    effect(() => {
      const pages = this.pages();
      if (!this.loading() && pages > 0 && this.page() > pages) this.page.set(pages);
    });
  }

  ngOnInit() {
    this.favourites.loadAll().subscribe({
      next: () => this.loading.set(false),
      error: (err) => {
        console.error('Error loading favourite albums:', err);
        this.error.set('Could not load your favourite albums. Please try again.');
        this.loading.set(false);
      }
    });
    this.musicdata.getGenres().subscribe({
      next: (genres) => this.genreNames.set(Object.fromEntries(genres.map(g => [g.id, g.name]))),
      error: (err) => console.error('Error fetching genres:', err)
    });
  }

  setQuery(text: string) {
    this.query.set(text);
    this.page.set(1);
  }

  selectGenre(id: number) {
    this.genre.set(id === this.genre() ? -1 : id);
    this.page.set(1);
  }

  setSort(sort: AlbumSort) {
    this.sort.set(sort);
    this.page.set(1);
  }

  setYear(year: number) {
    this.year.set(year);
    this.page.set(1);
  }

  setPage(page: number) {
    this.page.set(page);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  }

  clearFilters() {
    this.query.set('');
    this.genre.set(-1);
    this.sort.set('top');
    this.year.set(0);
    this.page.set(1);
  }

  toggleFavourite(album: { id: string; name: string }) {
    this.favourites.toggle(album.id, album.name);
  }
}
